export type ChainRelation = {
  id: string;
  itemIds: [string, string];
};

export type BranchRelation = {
  id: string;
  originId: string;
  mainPathIds: string[];
  branchPathIds: string[];
};

export type AlternativeRelation = {
  id: string;
  originId: string;
  alternativeId: string;
};

export type SetlistStructures = {
  chains: ChainRelation[];
  branches: BranchRelation[];
  alternatives: AlternativeRelation[];
};

export const emptyStructures = (): SetlistStructures => ({
  chains: [],
  branches: [],
  alternatives: [],
});

export function chainForItem(structures: SetlistStructures, itemId: string) {
  return structures.chains.find((chain) => chain.itemIds.includes(itemId));
}

export function chainBetween(
  structures: SetlistStructures,
  firstId: string,
  secondId: string,
) {
  return structures.chains.some(
    (chain) => chain.itemIds[0] === firstId && chain.itemIds[1] === secondId,
  );
}

export function branchFromOrigin(structures: SetlistStructures, itemId: string) {
  return structures.branches.find((branch) => branch.originId === itemId);
}

export function branchForItem(structures: SetlistStructures, itemId: string) {
  return structures.branches.find((branch) =>
    [branch.originId, ...branch.mainPathIds, ...branch.branchPathIds].includes(itemId),
  );
}

export function alternativeForItem(structures: SetlistStructures, itemId: string) {
  return structures.alternatives.find(
    (relation) => relation.originId === itemId || relation.alternativeId === itemId,
  );
}

export function removeChain(structures: SetlistStructures, itemId: string) {
  return {
    ...structures,
    chains: structures.chains.filter((chain) => !chain.itemIds.includes(itemId)),
  };
}

export function removeBranch(structures: SetlistStructures, itemId: string) {
  const branch = branchForItem(structures, itemId);
  return branch
    ? {
        ...structures,
        branches: structures.branches.filter((candidate) => candidate.id !== branch.id),
      }
    : structures;
}

export function removeAlternative(structures: SetlistStructures, itemId: string) {
  return {
    ...structures,
    alternatives: structures.alternatives.filter(
      (relation) => relation.originId !== itemId && relation.alternativeId !== itemId,
    ),
  };
}

export function branchSecondaryIds(structures: SetlistStructures) {
  return new Set(structures.branches.flatMap((branch) => branch.branchPathIds));
}

export function structuralMoveIds(structures: SetlistStructures, itemId: string) {
  return chainForItem(structures, itemId)?.itemIds ?? [itemId];
}

export function createChain(
  structures: SetlistStructures,
  firstId: string,
  secondId: string,
): SetlistStructures {
  const withoutItems = structures.chains.filter(
    (chain) => !chain.itemIds.includes(firstId) && !chain.itemIds.includes(secondId),
  );
  return {
    ...structures,
    chains: [
      ...withoutItems,
      { id: `chain-${firstId}-${secondId}`, itemIds: [firstId, secondId] },
    ],
  };
}

export function createAlternative(
  structures: SetlistStructures,
  originId: string,
  alternativeId: string,
): SetlistStructures {
  return {
    ...structures,
    alternatives: [
      ...structures.alternatives.filter(
        (relation) =>
          relation.originId !== originId && relation.alternativeId !== alternativeId,
      ),
      {
        id: `alternative-${originId}-${alternativeId}`,
        originId,
        alternativeId,
      },
    ],
  };
}

export function appendToSelectedBranch(
  structures: SetlistStructures,
  selectedId: string,
  newId: string,
) {
  const next = structuredClone(structures);
  const branch = next.branches.find((relation) =>
    [...relation.mainPathIds, ...relation.branchPathIds].includes(selectedId),
  );
  if (!branch) return next;
  const path = branch.mainPathIds.includes(selectedId)
    ? branch.mainPathIds
    : branch.branchPathIds;
  path.splice(path.indexOf(selectedId) + 1, 0, newId);
  return next;
}
